/**
 * CogoAgent — Phase B pilot agent UI panel for cogo-starter.
 *
 * Lightweight drawer + composer that:
 *   1. Reads the WebMCP tool registry via <WebMCPProvider>
 *   2. Sends the user's NL message to the configured llmAdapter
 *   3. Shows proposed tool calls as a reviewable list
 *   4. Honors `confirmationPolicy`: auto-execute read-only, prompt on writes
 *
 * Intentionally *not* the full react-renderer-v2 <CogoAgent />: that one
 * lives inside the AppV2 runtime and ties into EmbeddedToolProvider. This
 * starter-level agent renders at the app root so the PROBE works even on
 * the template-gallery landing page.
 *
 * NOTE: all icons are inline SVG on purpose. Starter idiomatic pattern
 * (see App.tsx back-arrow) — no runtime emoji, no lucide-react dep.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWebMCPContext, type ToolManifestEntry } from "@cogo/webmcp-runtime/react"
import {
  STARTER_AGENT_CONFIG,
  type AgentConfig,
  type ProposedToolCall,
} from "./agent-config"

// ── Transcript model ────────────────────────────────────────

type TranscriptEntry =
  | { id: string; kind: "user"; text: string }
  | { id: string; kind: "reply"; text: string }
  | {
      id: string
      kind: "tool"
      toolName: string
      status: "pending" | "running" | "ok" | "error" | "rejected"
      args: Record<string, unknown>
      result?: unknown
      error?: string
    }

interface CogoAgentProps {
  /** Override default config for testing / per-app tweaks. */
  config?: AgentConfig
  /** Hide the panel entirely. Useful for E2E tests that bypass the FAB. */
  disabled?: boolean
  /** Start with the drawer open — used by tests. */
  defaultOpen?: boolean
}

// ── Icons (inline SVG, starter-idiomatic) ──────────────────

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 2 11 13" />
      <path d="m22 2-7 20-4-9-9-4Z" />
    </svg>
  )
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    </svg>
  )
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}

// ── Main component ─────────────────────────────────────────

export function CogoAgent({ config = STARTER_AGENT_CONFIG, disabled, defaultOpen }: CogoAgentProps) {
  const { registry } = useWebMCPContext()
  const [isOpen, setIsOpen] = useState(!!defaultOpen)
  const [input, setInput] = useState("")
  const [busy, setBusy] = useState(false)
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([])
  const [toolCount, setToolCount] = useState(registry.size)
  const [pending, setPending] = useState<ProposedToolCall | null>(null)
  const transcriptEndRef = useRef<HTMLDivElement | null>(null)

  // Reactive tool-count + initial sync
  useEffect(() => {
    const unsub = registry.onToolsChanged((tools) => setToolCount(tools.length))
    setToolCount(registry.size)
    return unsub
  }, [registry])

  // Keep view pinned to the bottom on new entries. jsdom doesn't implement
  // scrollIntoView — gate on typeof so unit tests don't crash.
  useEffect(() => {
    const el = transcriptEndRef.current
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "end" })
    }
  }, [transcript, pending])

  const append = useCallback((entry: TranscriptEntry) => {
    setTranscript((prev) => [...prev, entry])
  }, [])

  const setEntryStatus = useCallback(
    (
      id: string,
      patch: Partial<Extract<TranscriptEntry, { kind: "tool" }>>,
    ) => {
      setTranscript((prev) =>
        prev.map((e) => (e.id === id && e.kind === "tool" ? { ...e, ...patch } : e)),
      )
    },
    [],
  )

  const executeCall = useCallback(
    async (call: ProposedToolCall) => {
      const entryId = `tool-${call.id}`
      append({
        id: entryId,
        kind: "tool",
        toolName: call.tool.name,
        status: "running",
        args: call.args,
      })
      try {
        const result = await registry.callTool(call.tool.name, call.args)
        setEntryStatus(entryId, { status: "ok", result })
      } catch (err) {
        setEntryStatus(entryId, {
          status: "error",
          error: err instanceof Error ? err.message : String(err),
        })
      }
    },
    [append, registry, setEntryStatus],
  )

  const approvePending = useCallback(async () => {
    if (!pending) return
    const call = pending
    setPending(null)
    setBusy(true)
    try {
      await executeCall(call)
    } finally {
      setBusy(false)
    }
  }, [pending, executeCall])

  const rejectPending = useCallback(() => {
    if (!pending) return
    append({
      id: `tool-${pending.id}`,
      kind: "tool",
      toolName: pending.tool.name,
      status: "rejected",
      args: pending.args,
    })
    setPending(null)
  }, [pending, append])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || busy || pending) return
    setInput("")
    setBusy(true)

    append({ id: `user-${Date.now()}`, kind: "user", text })

    try {
      const manifest = registry.getManifest() as ToolManifestEntry[]
      const { reply, toolCalls } = await config.llmAdapter.sendMessage({
        userMessage: text,
        tools: manifest,
      })
      if (reply) append({ id: `reply-${Date.now()}`, kind: "reply", text: reply })

      for (const call of toolCalls) {
        const verdict = config.confirmationPolicy(call)
        if (verdict === "confirm") {
          // Stop the loop at the first confirm — keep the user in control.
          setPending(call)
          return
        }
        await executeCall(call)
      }
    } catch (err) {
      append({
        id: `reply-${Date.now()}`,
        kind: "reply",
        text: `Adapter error: ${err instanceof Error ? err.message : String(err)}`,
      })
    } finally {
      setBusy(false)
    }
  }, [input, busy, pending, append, registry, config, executeCall])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        void handleSend()
      }
    },
    [handleSend],
  )

  const personaLabel = useMemo(() => config.persona.toUpperCase(), [config.persona])

  if (disabled) return null

  return (
    <>
      {/* FAB */}
      {!isOpen && (
        <button
          type="button"
          data-testid="cogo-agent-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Cogo Agent"
          className="fixed bottom-6 right-6 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
        >
          <SparkleIcon className="h-6 w-6" />
        </button>
      )}

      {/* Drawer */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Cogo Agent"
          data-testid="cogo-agent-drawer"
          className="fixed bottom-6 right-6 z-[9999] flex h-[min(600px,calc(100vh-48px))] w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary">
                <SparkleIcon className="h-3 w-3" />
                {personaLabel}
              </span>
              <span className="text-[11px] text-muted-foreground" data-testid="cogo-agent-tool-count">
                {toolCount} {toolCount === 1 ? "tool" : "tools"} · {config.llmAdapter.id}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close Cogo Agent"
              className="text-muted-foreground hover:text-foreground"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Transcript */}
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
            {transcript.length === 0 && !pending && (
              <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                Ask the assistant to use one of the {toolCount} available tools.
                Examples: <span className="font-mono">clear cart</span>,{" "}
                <span className="font-mono">remove item</span>.
              </div>
            )}
            {transcript.map((e) =>
              e.kind === "user" ? (
                <div key={e.id} className="flex justify-end">
                  <div className="max-w-[85%] rounded-lg bg-primary px-3 py-2 text-primary-foreground">{e.text}</div>
                </div>
              ) : e.kind === "reply" ? (
                <div key={e.id} className="max-w-[95%] rounded-lg bg-muted px-3 py-2 text-foreground">
                  {e.text}
                </div>
              ) : (
                <div key={e.id} className="rounded-md border border-border bg-background p-2 text-xs">
                  <div className="flex items-center gap-2">
                    <ShieldIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="font-mono font-semibold">{e.toolName}</span>
                    <ToolStatusBadge status={e.status} />
                  </div>
                  {Object.keys(e.args).length > 0 && (
                    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all text-[11px] text-muted-foreground">
                      {JSON.stringify(e.args, null, 2)}
                    </pre>
                  )}
                  {e.status === "error" && e.error && (
                    <p className="mt-1 text-[11px] text-destructive">{e.error}</p>
                  )}
                </div>
              ),
            )}

            {pending && (
              <div
                role="alert"
                data-testid="cogo-agent-confirmation"
                className="rounded-md border border-amber-500/40 bg-amber-50/60 p-3 text-xs dark:bg-amber-900/20"
              >
                <div className="flex items-center gap-2 font-medium">
                  <AlertIcon className="h-4 w-4 text-amber-600" />
                  Confirm action
                </div>
                <p className="mt-1 text-muted-foreground">
                  The agent wants to call{" "}
                  <span className="font-mono text-foreground">{pending.tool.name}</span>. This is a write
                  action — please review and approve.
                </p>
                {Object.keys(pending.args).length > 0 && (
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-background p-2 text-[11px]">
                    {JSON.stringify(pending.args, null, 2)}
                  </pre>
                )}
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    data-testid="cogo-agent-approve"
                    onClick={approvePending}
                    className="rounded-md bg-primary px-3 py-1 text-[12px] font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    data-testid="cogo-agent-reject"
                    onClick={rejectPending}
                    className="rounded-md border border-border bg-background px-3 py-1 text-[12px] font-medium hover:bg-muted"
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}
            <div ref={transcriptEndRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <textarea
                data-testid="cogo-agent-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={busy || !!pending}
                rows={1}
                placeholder={pending ? "Resolve the pending action first…" : "Ask the agent…"}
                className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-50"
              />
              <button
                type="button"
                data-testid="cogo-agent-send"
                onClick={handleSend}
                disabled={busy || !!pending || !input.trim()}
                aria-label="Send message"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <SendIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Status badge ────────────────────────────────────────────

function ToolStatusBadge({ status }: { status: "pending" | "running" | "ok" | "error" | "rejected" }) {
  const map: Record<typeof status, { label: string; cls: string }> = {
    pending: { label: "pending", cls: "bg-muted text-muted-foreground" },
    running: { label: "running", cls: "bg-blue-500/10 text-blue-600" },
    ok: { label: "ok", cls: "bg-emerald-500/10 text-emerald-600" },
    error: { label: "error", cls: "bg-destructive/10 text-destructive" },
    rejected: { label: "rejected", cls: "bg-amber-500/10 text-amber-600" },
  }
  const { label, cls } = map[status]
  return (
    <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>
  )
}
